/* eslint-disable react/no-array-index-key, consistent-return, @typescript-eslint/no-shadow, prettier/prettier, @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useHistory as useRouterHistory } from 'react-router-dom';
import { useStyletron } from 'baseui';
import { Button, KIND, SIZE } from 'baseui/button';
import { DASHBOARD_RESOURCES, PAGE_NAMES } from 'constant';
import { IZkChangeLog } from '../../utils/types';
import { formatDate, formatJson } from '../../utils/formatters';

interface ChangeTimelineProps {
	changes: IZkChangeLog[];
}

const ANALYZE_PATH = `/${PAGE_NAMES.dashboard}/${DASHBOARD_RESOURCES.configImpact}/analyze`;

const ChangeTimeline: React.FC<ChangeTimelineProps> = ({ changes }) => {
	const [css] = useStyletron();
	const routerHistory = useRouterHistory();
	const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

	if (!changes?.length) {
		return (
			<div className={css({ padding: '32px', textAlign: 'center' as const, color: '#666' })}>
				No changes found matching your filters.
			</div>
		);
	}

	return (
		<div className={css({ position: 'relative' as const, paddingLeft: '24px' })}>
			{/* Vertical line */}
			<div
				className={css({
					position: 'absolute' as const,
					left: '8px',
					top: '0',
					bottom: '0',
					width: '2px',
					backgroundColor: '#E2E2E2'
				})}
			/>

			{changes.map((change, idx) => (
				<div
					key={`${change.keyName}-${change.eventTime}`}
					className={css({
						position: 'relative' as const,
						marginBottom: '24px',
						paddingLeft: '16px'
					})}>
					{/* Circle marker */}
					<div
						className={css({
							position: 'absolute' as const,
							left: '-20px',
							top: '4px',
							width: '12px',
							height: '12px',
							borderRadius: '50%',
							backgroundColor: '#276EF1',
							border: '2px solid #FFF',
							boxShadow: '0 0 0 2px #276EF1'
						})}
					/>

					{/* Entry card */}
					<div
						role="button"
						tabIndex={0}
						className={css({
							border: '1px solid #E2E2E2',
							borderRadius: '8px',
							padding: '12px 16px',
							cursor: 'pointer',
							transition: 'box-shadow 0.2s',
							':hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }
						})}
						onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								setExpandedIdx(expandedIdx === idx ? null : idx);
							}
						}}>
						{/* Header */}
						<div className={css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' })}>
							<div className={css({ display: 'flex', alignItems: 'center', gap: '12px' })}>
								<code className={css({ fontSize: '14px', fontWeight: 600, color: '#276EF1' })}>{change.keyName}</code>
								<span
									className={css({
										backgroundColor: '#F0F0F0',
										padding: '2px 8px',
										borderRadius: '4px',
										fontSize: '12px',
										color: '#666'
									})}>
									{change.serviceName}
								</span>
							</div>
							<span className={css({ fontSize: '13px', color: '#999' })}>{formatDate(change.eventTime)}</span>
						</div>

						{/* Expanded diff */}
						{expandedIdx === idx && (
							<div className={css({ marginTop: '12px' })}>
								<div
									className={css({
										display: 'grid',
										gridTemplateColumns: '1fr 1fr',
										gap: '8px',
										marginBottom: '12px'
									})}>
									<div
										className={css({
											backgroundColor: '#FFF3F0',
											padding: '8px 12px',
											borderRadius: '6px',
											fontSize: '13px',
											fontFamily: 'monospace'
										})}>
										<div className={css({ color: '#999', fontSize: '11px', marginBottom: '4px' })}>Old Value</div>
										<pre className={css({ margin: '0', whiteSpace: 'pre-wrap' as const })}>
											{formatJson(change.oldValue)}
										</pre>
									</div>
									<div
										className={css({
											backgroundColor: '#E6F7ED',
											padding: '8px 12px',
											borderRadius: '6px',
											fontSize: '13px',
											fontFamily: 'monospace'
										})}>
										<div className={css({ color: '#999', fontSize: '11px', marginBottom: '4px' })}>New Value</div>
										<pre className={css({ margin: '0', whiteSpace: 'pre-wrap' as const })}>
											{formatJson(change.newValue)}
										</pre>
									</div>
								</div>
								<Button
									kind={KIND.secondary}
									size={SIZE.mini}
									onClick={(e: React.MouseEvent) => {
										e.stopPropagation();
										routerHistory.push(`${ANALYZE_PATH}?key=${encodeURIComponent(change.keyName)}`);
									}}>
									Re-analyze
								</Button>
							</div>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

export default ChangeTimeline;
